import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloud, faShieldHalved, faInfinity, faRobot, faCheck } from '@fortawesome/free-solid-svg-icons';

export default function Projects() {
  const projects = [
    {
      title: "Enterprise GitOps & Multi-Cloud IaC Platform",
      category: "Platform Engineering · GitOps",
      icon: faInfinity,
      description: "Designed a declarative Infrastructure-as-Code delivery platform leveraging Atlantis for PR-driven Terraform reviews and Terragrunt module orchestration.",
      problem: "Fragmented infrastructure configurations, manual approval bottlenecks, and state drifts across multi-cloud environments.",
      solution: "Implemented automated PR plan/apply workflows in Atlantis with automated linting, Trivy vulnerability scans, and declarative ArgoCD GitOps sync for Kubernetes clusters.",
      impact: "Reduced infrastructure provisioning lead time by 80% while establishing an auditable, immutable infrastructure delivery pipeline.",
      stack: ["Terraform", "Terragrunt", "Atlantis", "ArgoCD", "Kubernetes", "GitLab CI"]
    },
    {
      title: "Zero-Trust Secret Governance & Cloud Security Posture",
      category: "Cloud Security · DevSecOps",
      icon: faShieldHalved,
      description: "Engineered an enterprise-grade secret management and vulnerability posture management system integrating HashiCorp Vault and Wiz CSPM.",
      problem: "Static credentials embedded in deployment pipelines and lack of proactive security guardrails across multi-cloud infrastructure.",
      solution: "Centralized dynamic short-lived secret leasing via HashiCorp Vault with automated Jenkins and Kubernetes pod identity integrations; enforced proactive security guardrails with Wiz and GCP SCC.",
      impact: "Eliminated static secrets across production environments, automated credential rotation, and reduced vulnerability remediation MTTR by 60%.",
      stack: ["HashiCorp Vault", "Wiz", "GCP SCC", "Trivy", "Jenkins", "Docker"]
    },
    {
      title: "Multi-Region Cloud Migration & DR Architecture",
      category: "Cloud Architecture · SRE",
      icon: faCloud,
      description: "Architected and executed an enterprise cloud migration from AWS to GCP, decomposing monolithic architectures into composable cloud-native microservices.",
      problem: "Single-region dependency, high operational overhead, and monolithic deployment coupling under legacy infrastructure.",
      solution: "Provisioned high-availability GKE clusters and Managed Instance Groups (MIGs) across availability zones with Cloud SQL and automated cross-region backup failover; implemented FinOps GKE CronJobs.",
      impact: "Achieved 99.99% system availability, zero-downtime migration cutover, and a 25%+ reduction in ongoing cloud infrastructure spend.",
      stack: ["Google Cloud (GCP)", "GKE", "Cloud SQL", "MIGs", "Prometheus", "FinOps"]
    },
    {
      title: "AI-Augmented Platform Engineering Workflows",
      category: "AI & Platform Automation",
      icon: faRobot,
      description: "Integrated state-of-the-art agentic AI tooling including Claude Code, Gemini Enterprise, and Copilot into DevOps development and troubleshooting loops.",
      problem: "Time-consuming manual incident runbook investigations, repetitive boilerplate scripting, and complex multi-cloud configuration drift analysis.",
      solution: "Developed streamlined AI-assisted CLI workflows with Claude Code for rapid log root-cause analysis, automated Terraform manifest generation, and intelligent runbook execution.",
      impact: "Accelerated root-cause analysis and automation script delivery, elevating developer productivity across cloud platform operations.",
      stack: ["Claude Code", "Gemini Enterprise", "GitHub Copilot", "Python", "Bash", "Linux"]
    }
  ];

  return (
    <section id="projects" className="py-16 md:py-24 text-white border-t border-zinc-800/80">
      {/* Section Header */}
      <div className="max-w-3xl mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-teal-500/10 text-teal-300 border border-teal-500/30 uppercase tracking-wider mb-3">
          Architecture &amp; Solutions
        </div>
        <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
          Featured Architecture Case Studies
        </h2>
        <p className="mt-2 text-base text-gray-400">
          Selected enterprise cloud infrastructure initiatives showcasing high-scale automation, security hardening, and resilient distributed systems.
        </p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {projects.map((item, idx) => (
          <div
            key={idx}
            className="flex flex-col justify-between p-7 sm:p-8 rounded-2xl bg-dark-200/50 border border-zinc-800 hover:border-teal-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-teal-500/5 group"
          >
            <div>
              {/* Header Badge & Icon */}
              <div className="flex items-center justify-between gap-4 mb-4">
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-teal-500/10 text-teal-300 border border-teal-500/20">
                  {item.category}
                </span>
                <div className="w-10 h-10 rounded-xl bg-dark-300 border border-zinc-700/60 flex items-center justify-center text-teal-400 group-hover:scale-110 group-hover:bg-teal-500/10 transition-all">
                  <FontAwesomeIcon icon={item.icon} className="text-base" />
                </div>
              </div>

              {/* Title & Description */}
              <h3 className="text-xl sm:text-2xl font-bold text-white group-hover:text-teal-300 transition-colors mb-3">
                {item.title}
              </h3>
              <p className="text-sm sm:text-base text-gray-300 leading-relaxed mb-6 font-normal">
                {item.description}
              </p>

              {/* Problem / Solution / Impact Cards */}
              <div className="space-y-3 mb-6 text-xs sm:text-sm">
                <div className="p-3 rounded-xl bg-dark-300/60 border border-zinc-800/80">
                  <span className="font-semibold text-gray-200 uppercase tracking-wider text-[11px] block mb-1">Challenge</span>
                  <p className="text-gray-400">{item.problem}</p>
                </div>
                <div className="p-3 rounded-xl bg-dark-300/60 border border-zinc-800/80">
                  <span className="font-semibold text-teal-300 uppercase tracking-wider text-[11px] block mb-1">Architecture Solution</span>
                  <p className="text-gray-300">{item.solution}</p>
                </div>
                <div className="p-3 rounded-xl bg-teal-500/5 border border-teal-500/20 flex items-start gap-2.5">
                  <FontAwesomeIcon icon={faCheck} className="text-teal-400 text-xs mt-1 shrink-0" />
                  <p className="text-gray-200 font-medium">{item.impact}</p>
                </div>
              </div>
            </div>

            {/* Stack Tags */}
            <div className="flex flex-wrap gap-2 pt-4 border-t border-zinc-800/80">
              {item.stack.map((tech, tIdx) => (
                <span
                  key={tIdx}
                  className="px-2.5 py-1 text-xs font-medium rounded-md bg-zinc-800/80 text-gray-300 border border-zinc-700/50"
                >
                  {tech}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
